import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

interface WelcomeAnimationProps {
  userName?: string;
  onComplete: () => void;
}

const WelcomeAnimation: React.FC<WelcomeAnimationProps> = ({
  userName,
  onComplete,
}) => {
  const [phase, setPhase] = useState<'enter' | 'show' | 'exit'>('enter');

  useEffect(() => {
    // Enter phase
    const enterTimer = setTimeout(() => setPhase('show'), 100);

    // Show phase duration
    const showTimer = setTimeout(() => setPhase('exit'), 2000);

    // Exit and complete
    const exitTimer = setTimeout(() => onComplete(), 2500);

    return () => {
      clearTimeout(enterTimer);
      clearTimeout(showTimer);
      clearTimeout(exitTimer);
    };
  }, [onComplete]);

  const displayName = userName?.split('@')[0] || 'there';

  const animation = (
    <div
      className={`fixed inset-0 z-[10000] flex items-center justify-center bg-white transition-opacity duration-500 ${
        phase === 'enter' ? 'opacity-0' : phase === 'exit' ? 'opacity-0' : 'opacity-100'
      }`}
    >
      <div
        className={`text-center transform transition-all duration-700 ${
          phase === 'enter'
            ? 'scale-50 opacity-0'
            : phase === 'exit'
            ? 'scale-110 opacity-0'
            : 'scale-100 opacity-100'
        }`}
      >
        {/* Animated whale mascot */}
        <div className="mb-6 animate-bounce">
          <img
            src="/whale-happy.png"
            alt="Welcome!"
            className="w-32 h-32 mx-auto object-contain"
          />
        </div>

        {/* Welcome text */}
        <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-3">
          Welcome back!
        </h1>
        <p className="text-xl text-gray-600">
          Hello, <span className="font-semibold text-blue-600">{displayName}</span>
        </p>

        {/* Decorative elements */}
        <div className="mt-8 flex justify-center gap-2">
          <span className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" style={{ animationDelay: '0ms' }} />
          <span className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" style={{ animationDelay: '150ms' }} />
          <span className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    </div>
  );

  return createPortal(animation, document.body);
};

export default WelcomeAnimation;
